import React, { useState, useEffect, useContext } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../Common/Button/Button";
import DynamicSelectInput from "../../../Common/Select-Input/SelectInput";
import DynamicTextField from "../../../Common/Textfield/Textfield";
import Table from "../../../Common/Table/Table";
import { get, post, deleteRequest, put } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  TAX_CONFIG_LIST,
  TAX_CONFIG_CREATE,
  TAX_CONFIG_DELETE,
  TAX_CONFIG_UPDATE,
} from "../../../helper/url_helper";
import { DynamicContext } from "../../../Context/DynamicContext";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import { ConfirmationPopup } from "../../../Common/PopupStyles/PopupComponent";
import editIcon from "../../../Assets/Images/cf-edit-icon-dark.svg";
import editLightIcon from "../../../Assets/Images/cf-edit-icon-light.svg";
import deleteIcon from "../../../Assets/Images/cf-delete-icon-dark.svg";
import deleteLightIcon from "../../../Assets/Images/cf-delete-icon-light.svg";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import { createPortal } from "react-dom";
import OptionsData from "../../../Assets/Json/StaticOptions.json";
const { StaticOptions = {} } = OptionsData || {};
const { tax_type = [] } = StaticOptions || {};

const countryValues = [
  { title: "India", value: "INDIA" },
  { title: "United States", value: "USA" },
  { title: "Canada", value: "CANADA" },
];

const taxCountryFilterValues = [
  { title: "All countries", value: "All countries" },
  ...countryValues,
];

const taxStatusFilterValues = [
  { title: "All statuses", value: "All statuses" },
  { title: "Active", value: "active" },
  { title: "Inactive", value: "inactive" },
];

const calculationTypeValues = [
  { title: "Percentage", value: "PERCENTAGE" },
  { title: "Flat Fee", value: "FLAT" },
];

const statusValues = [
  { title: "Active", value: "active" },
  { title: "Inactive", value: "inactive" },
];

const inputStyle = {
  borderRadius: "12px",
  color: "var(--header__color)",
  fontSize: "14px",
  height: "38px",
  padding: "0 12px",
};

const labelStyle = {
  color: "var(--sidepopup-label-color)",
  fontSize: "12px",
  fontWeight: 500,
  marginBottom: "6px",
};

const selectSx = {
  "&&": {
    border: "none !important",
    boxShadow: "none !important",
  },
  "&.MuiOutlinedInput-root": {
    border: "none !important",
    boxShadow: "none !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid var(--border__color) !important",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "1px solid rgb(142, 136, 244) !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #6860ee !important",
  },
};

const normalizeTaxCountry = (country) =>
  String(country || "")
    .trim()
    .toUpperCase();

const getTaxRateUnit = (calculationType, country) => {
  if (calculationType === "PERCENTAGE") {
    return "%";
  }

  if (calculationType === "FLAT") {
    return ["USA", "UNITED STATES", "CANADA"].includes(
      normalizeTaxCountry(country),
    )
      ? "$"
      : "\u20B9";
  }

  return "";
};

function TaxConfiguration() {
  const { isDarkMode } = useContext(DynamicContext);
  const [taxSearch, setTaxSearch] = useState("");
  const [taxValue, setTaxValue] = useState([]);
  const [taxCountryFilter, setTaxCountryFilter] = useState("All countries");
  const [taxStatusFilter, setTaxStatusFilter] = useState("All statuses");
  const [isLoading, setIsLoading] = useState(false);
  const [isTaxPopupOpen, setIsTaxPopupOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [deleteTaxId, setDeleteTaxId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const createTaxConfig = async (values, resetForm) => {
    try {
      await post(VYLABS_BASE_URL, TAX_CONFIG_CREATE, values);
      Toast("success", "Tax configuration created successfully");
      await getConfigList();
      setIsTaxPopupOpen(false);
      resetForm();
    } catch (error) {
      console.error("Tax configuration create failed:", error);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to create tax configuration",
      );
    }
  };

  const updateTaxConfig = async (values, resetForm) => {
    if (!selectedTax?.id) return;

    try {
      await put(
        VYLABS_BASE_URL,
        `${TAX_CONFIG_UPDATE}?id=${encodeURIComponent(selectedTax.id)}`,
        values,
      );
      Toast("success", "Tax configuration updated successfully");
      await getConfigList();
      setIsTaxPopupOpen(false);
      setSelectedTax(null);
      resetForm();
    } catch (error) {
      console.error("Tax configuration update failed:", error);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to update tax configuration",
      );
    }
  };

  const taxFormik = useFormik({
    initialValues: {
      country: "",
      tax_type: "",
      tax_name: "",
      region: "",
      region_code: "",
      calculation_type: "",
      tax_value: "",
      is_active: true,
    },
    validationSchema: Yup.object({
      country: Yup.string().required("Country is required"),
      calculation_type: Yup.string().required("Calculation type is required"),
      tax_name: Yup.string().when("tax_type", {
        is: "OTHER",
        then: (schema) => schema.required("Tax name is required"),
        otherwise: (schema) => schema,
      }),
      tax_value: Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null ? null : value,
        )
        .typeError("Rate must be a valid number")
        .when("calculation_type", {
          is: "PERCENTAGE",
          then: (schema) => schema.max(100, "Percentage cannot exceed 100"),
          otherwise: (schema) => schema,
        })
        .required("Rate is required")
        .nullable(),
      tax_type: Yup.string().required("Tax type is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        calculation_type: values.calculation_type,
        country: values.country,
        is_active: values.is_active,
        region: values.region?.trim() || "",
        region_code: values.region_code?.trim() || "",
        tax_name: values.tax_name?.trim() || "",
        tax_type: values.tax_type?.trim() || "",
        tax_value:
          values.tax_value === "" || values.tax_value === null
            ? 0
            : Number.parseFloat(values.tax_value),
      };

      if (selectedTax?.id) {
        await updateTaxConfig(payload, resetForm);
        return;
      }

      await createTaxConfig(payload, resetForm);
    },
  });

  const handleSubmitTaxConfiguration = async (event) => {
    event.preventDefault();
    const errors = await taxFormik.validateForm();

    if (Object.keys(errors).length > 0) {
      taxFormik.setTouched(
        {
          country: true,
          calculation_type: true,
          tax_name: true,
          tax_type: true,
          tax_value: true,
        },
        false,
      );
      Toast("error", "Please fill all required fields correctly");
      return;
    }

    taxFormik.handleSubmit(event);
  };

  const getConfigList = async () => {
    try {
      setIsLoading(true);
      const response = await get(VYLABS_BASE_URL, TAX_CONFIG_LIST);
      const data = Array.isArray(response?.tax_configs)
        ? response.tax_configs
        : [];
      setTaxValue(data);
    } catch (error) {
      console.error("taxConfig", error);
      setTaxValue([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getConfigList();
  }, []);

  const getFieldError = (fieldName) =>
    taxFormik.touched[fieldName] && Boolean(taxFormik.errors[fieldName]);

  const handleRequiredSelectChange = (fieldName) => (event) => {
    const value = event.target.value;

    taxFormik.setFieldValue(fieldName, value);
    if (
      fieldName === "calculation_type" &&
      value === "PERCENTAGE" &&
      Number(taxFormik.values.tax_value) > 100
    ) {
      taxFormik.setFieldValue("tax_value", "100");
    }
    if (value) {
      taxFormik.setFieldError(fieldName, "");
    }
  };

  const handleTaxValueChange = (event) => {
    const value = event.target.value;

    if (
      taxFormik.values.calculation_type === "PERCENTAGE" &&
      value !== "" &&
      Number(value) > 100
    ) {
      taxFormik.setFieldValue("tax_value", "100");
      return;
    }

    taxFormik.handleChange(event);
  };

  const handleOpenCreateTax = () => {
    setSelectedTax(null);
    taxFormik.resetForm();
    setIsTaxPopupOpen(true);
  };

  const handleCloseTaxPopup = () => {
    setIsTaxPopupOpen(false);
    setSelectedTax(null);
    taxFormik.resetForm();
  };

  const handleEditTax = (row) => {
    setSelectedTax(row);
    taxFormik.setValues({
      ...taxFormik.initialValues,
      country: row.country || "",
      tax_type: row.tax_type || "",
      tax_name: row.tax_name || "",
      region: row.region || "",
      region_code: row.region_code || "",
      calculation_type: row.calculation_type || "",
      tax_value:
        row.tax_value === undefined || row.tax_value === null
          ? ""
          : String(row.tax_value),
      is_active: Boolean(row.is_active),
    });
    setIsTaxPopupOpen(true);
  };

  const handleDeleteTax = async () => {
    if (!deleteTaxId) return;

    try {
      const response = await deleteRequest(
        VYLABS_BASE_URL,
        `${TAX_CONFIG_DELETE}?id=${deleteTaxId}`,
      );

      if (response) {
        Toast("success", "Tax configuration deleted successfully");
        getConfigList();
        setIsDeleteOpen(false);
        setDeleteTaxId(null);
      }
    } catch (error) {
      console.error("error in deleteTaxConfig:", error);
      Toast("error", "Failed to delete tax configuration");
    }
  };

  const taxColumns = [
      {
        name: "COUNTRY",
        selector: (row) => row.country || "--",
        sortable: true,
      },
      {
        name: "TAX",
        selector: (row) => row.tax_name || row.tax_type || "--",
        cell: (row) => (
          <div>
            <div style={{ color: "var(--header__color)", fontWeight: 600 }}>
              {row.tax_name || row.tax_type || "--"}
            </div>
            <div style={{ color: "var(--grid-na-text)", fontSize: "12px" }}>
              {row.tax_type || "--"}
            </div>
          </div>
        ),
        sortable: true,
      },     
      {
        name: "REGION",
        selector: (row) => row.region_code || "--",
        sortable: true,
      },
      {
        name: "CURRENCY",
        selector: (row) => row.currency || "--",
        sortable: true,
      },
      {
        name: "CALCULATION",
        selector: (row) => row.calculation_type || "--",
        sortable: true,
      },
      {
        name: "RATE",
        selector: (row) => row.tax_value ?? "--",
        cell: (row) => {
          if (row.tax_value === null || row.tax_value === undefined) {
            return "--";
          }

          return row.calculation_type === "PERCENTAGE"
            ? `${row.tax_value}%`
            : row.tax_value;
        },
        sortable: true,
      },
      {
        name: "STATUS",
        selector: (row) => (row.is_active ? "Active" : "Inactive"),
        cell: (row) => {
          const isActive = Boolean(row.is_active);

          return (
            <span
              style={{
                backgroundColor: isActive ? "#dcfce7" : "#f3f4f6",
                border: `1px solid ${isActive ? "#86efac" : "#e5e7eb"}`,
                borderRadius: "999px",
                color: isActive ? "#16a34a" : "#6b7280",
                fontSize: "12px",
                fontWeight: 500,
                padding: "3px 10px",
              }}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
        sortable: true,
      },
      {
        name: "Actions",
        minWidth: "110px",
        cell: (row) => (
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "80px",
            }}
          >
            <img
              src={isDarkMode ? editIcon : editLightIcon}
              alt="Edit"
              style={{
                cursor: "pointer",
                opacity: 0.7,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 1)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
              onClick={(e) => {
                e.stopPropagation();
                handleEditTax(row);
              }}
            />
            <img
              src={isDarkMode ? deleteIcon : deleteLightIcon}
              alt="Delete"
              style={{
                cursor: "pointer",
                opacity: 0.7,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 1)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTaxId(row.id);
                setIsDeleteOpen(true);
              }}
            />
          </div>
        ),
      },
    ];

  const filteredTaxRules = taxValue.filter((rule) => {
    const normalizedSearch = taxSearch.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      rule.tax_name?.toLowerCase().includes(normalizedSearch) ||
      rule.tax_type?.toLowerCase().includes(normalizedSearch) ||
      rule.country?.toLowerCase().includes(normalizedSearch) ||
      rule.currency?.toLowerCase().includes(normalizedSearch) ||
      rule.region_code?.toLowerCase().includes(normalizedSearch);
    const matchesCountry =
      taxCountryFilter === "All countries" || rule.country === taxCountryFilter;
    const statusValue = rule.is_active ? "active" : "inactive";
    const matchesStatus =
      taxStatusFilter === "All statuses" || statusValue === taxStatusFilter;

    return matchesSearch && matchesCountry && matchesStatus;
  });

  const taxRateUnit = getTaxRateUnit(
    taxFormik.values.calculation_type,
    taxFormik.values.country,
  );
  const taxRateLabel = taxRateUnit ? `Rate (${taxRateUnit})` : "Rate";

  return (
    <div>
      <div
        style={{
          alignItems: "flex-start",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "26px",
        }}
      >
        <div>
          <h3
            style={{
              color: "var(--header__color)",
              fontSize: "18px",
              fontWeight: 600,
              margin: "0 0 8px",
            }}
          >
            Tax Configuration
          </h3>
          <p
            style={{
              color: "var(--grid-na-text)",
              fontSize: "13px",
              margin: 0,
            }}
          >
            Create and manage reusable tax rules for invoices.
          </p>
        </div>
        <Button label="Add Tax" event={handleOpenCreateTax} />
      </div>

      <div
        style={{
          display: "grid",
          gap: "12px",
          gridTemplateColumns: "minmax(240px, 1fr) 155px 138px",
          marginBottom: "14px",
        }}
      >
        <input
          type="text"
          value={taxSearch}
          onChange={(event) => setTaxSearch(event.target.value)}
          placeholder="Search tax name or code..."
          style={{
            background: "var(--body__background)",
            border: "1px solid var(--border__color)",
            borderRadius: "12px",
            color: "var(--header__color)",
            fontFamily: "inherit",
            fontSize: "14px",
            height: "38px",
            outline: "none",
            padding: "0 14px",
          }}
        />
        <DynamicSelectInput
          value={taxCountryFilter}
          options={taxCountryFilterValues}
          onChange={(event) => setTaxCountryFilter(event.target.value)}
          menuWidth={180}
          selectStyle={{
            borderRadius: "12px",
            color: "var(--header__color)",
            fontSize: "14px",
          }}
        />
        <DynamicSelectInput
          value={taxStatusFilter}
          options={taxStatusFilterValues}
          onChange={(event) => setTaxStatusFilter(event.target.value)}
          menuWidth={170}
          selectStyle={{
            borderRadius: "12px",
            color: "var(--header__color)",
            fontSize: "14px",
          }}
        />
      </div>

      <Table
        columns={taxColumns}
        data={filteredTaxRules}
        pagination={false}
        progressPending={isLoading}
        backgroundColor="var(--body__background)"
        color="var(--grid-na-text)"
        customStyle={{
          headCells: {
            borderBottom: "1px solid var(--border__color)",
            color: "var(--grid-na-text)",
            fontSize: "12px",
            fontWeight: 700,
            minHeight: "44px",
          },
          cells: {
            borderBottom: "1px solid var(--border__color)",
            color: "var(--grid-na-text)",
            fontSize: "14px",
            minHeight: "60px",
          },
        }}
        dataNotFoundMessage="No tax rules found"
      />

      {createPortal(
      <SidePopup
        isOpen={isTaxPopupOpen}
        onClose={handleCloseTaxPopup}
        title={"Tax Configuration"}
        sidePopupWidth="70%"
        contentPadding="24px"
      >
        <form
          onSubmit={handleSubmitTaxConfiguration}
          style={{ padding: 0 }}
          noValidate
        >
          <div
            style={{
              alignItems: "flex-start",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "26px",
            }}
          >
            <div>
              <h3
                style={{
                  color: "var(--header__color)",
                  fontSize: "16px",
                  fontWeight: 600,
                  margin: "0 0 8px",
                }}
              >
                Tax Configuration
              </h3>
              <p
                style={{
                  color: "var(--grid-na-text)",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                Configure the reusable tax rule used while generating invoices.
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px 16px",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            }}
          >
            <DynamicSelectInput
              label="Country"
              name="country"
              value={taxFormik.values.country}
              options={countryValues}
              onChange={handleRequiredSelectChange("country")}
              onBlur={() => taxFormik.setFieldTouched("country", true)}
              required
              error={getFieldError("country") && !taxFormik.values.country}
              errorMessage={taxFormik.errors.country}
              labelStyle={labelStyle}
              menuWidth={360}
              selectStyle={{
                border: "none",
                borderRadius: "12px",
                boxShadow: "none",
                color: "var(--header__color)",
                fontSize: "14px",
              }}
              selectSx={selectSx}
            />
            <DynamicSelectInput
              label="Tax Type"
              name="tax_type"
              options={tax_type}
              value={taxFormik.values.tax_type}
              onChange={handleRequiredSelectChange("tax_type")}
              onBlur={() => taxFormik.setFieldTouched("tax_type", true)}
              error={getFieldError("tax_type")}
              errorMessage={taxFormik.errors.tax_type}
              labelStyle={labelStyle}
              style={inputStyle}
              required
            />
            <DynamicTextField
              label="Tax Name"
              name="tax_name"
              value={taxFormik.values.tax_name}
              onChange={taxFormik.handleChange}
              onBlur={() => taxFormik.setFieldTouched("tax_name", true)}
              error={getFieldError("tax_name")}
              errorMessage={taxFormik.errors.tax_name}
              labelStyle={labelStyle}
              style={inputStyle}
              required={taxFormik.values.tax_type === "OTHER" ? true : false}
            />
            <DynamicTextField
              label="Region"
              name="region"
              value={taxFormik.values.region}
              onChange={taxFormik.handleChange}
              onBlur={() => taxFormik.setFieldTouched("region", true)}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="Region Code"
              name="region_code"
              value={taxFormik.values.region_code}
              onChange={taxFormik.handleChange}
              onBlur={() => taxFormik.setFieldTouched("region_code", true)}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicSelectInput
              label="Calculation"
              name="calculation_type"
              value={taxFormik.values.calculation_type}
              options={calculationTypeValues}
              onChange={handleRequiredSelectChange("calculation_type")}
              onBlur={() =>
                taxFormik.setFieldTouched("calculation_type", true)
              }
              required
              error={
                getFieldError("calculation_type") &&
                !taxFormik.values.calculation_type
              }
              errorMessage={taxFormik.errors.calculation_type}
              labelStyle={labelStyle}
              menuWidth={260}
              selectStyle={{
                border: "none",
                borderRadius: "12px",
                boxShadow: "none",
                color: "var(--header__color)",
                fontSize: "14px",
              }}
              selectSx={selectSx}
            />
            <DynamicTextField
              label={taxRateLabel}
              name="tax_value"
              type="number"
              value={taxFormik.values.tax_value}
              onChange={handleTaxValueChange}
              onBlur={() => taxFormik.setFieldTouched("tax_value", true)}
              error={getFieldError("tax_value")}
              errorMessage={taxFormik.errors.tax_value}
              labelStyle={labelStyle}
              style={inputStyle}
              required
            />
            <DynamicSelectInput
              label="Status"
              name="is_active"
              value={taxFormik.values.is_active ? "active" : "inactive"}
              options={statusValues}
              onChange={(event) =>
                taxFormik.setFieldValue(
                  "is_active",
                  event.target.value === "active",
                )
              }
              onBlur={() => taxFormik.setFieldTouched("is_active", true)}
              labelStyle={labelStyle}
              menuWidth={220}
              selectStyle={{
                border: "none",
                borderRadius: "12px",
                boxShadow: "none",
                color: "var(--header__color)",
                fontSize: "14px",
              }}
              selectSx={selectSx}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "24px",
            }}
          >
            <Button
              type="button"
              label="Cancel"
              isCancel
              event={handleCloseTaxPopup}
            />
            <Button
              type="submit"
              label={
                taxFormik.isSubmitting
                  ? selectedTax
                    ? "Updating..."
                    : "Creating..."
                  : selectedTax
                    ? "Update"
                    : "Create"
              }
              disabled={taxFormik.isSubmitting}
            />
          </div>
        </form>
     </SidePopup>,
      document.body,
      )}
      {createPortal(
        <ConfirmationPopup
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteTaxId(null);
          }}
          title="Delete Tax Configuration"
          subTitle="This tax configuration will be permanently deleted and cannot be recovered."
          message="Are you sure you want to delete this tax configuration?"
          confirmLabel="Delete"
          onConfirm={handleDeleteTax}
        />,
        document.body,
      )}
    </div>
  );
}

export default TaxConfiguration;
